import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { audit } from "../../api/audit";
import { useTranslationHook } from '../../hooks';
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";

interface AuditItem {
  recordId: string;
  createdBy: string;
  createdOn: string;
  schemaName: string;
  tableName: string;
  operationType: string;
  originalValues: string;
  newValues: string;
}

const AuditTable: React.FC = () => {
  const { t } = useTranslationHook();
  const handleError = useErrorHandler();
  const [data, setData] = useState<AuditItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({
    tableName: "",
    operationType: "",
    accountId: "",
    userId: "",
    userName: "",
    startDate: "",
    endDate: "",
  });

  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [jsonDialogTitle, setJsonDialogTitle] = useState("");
  const [jsonDialogContent, setJsonDialogContent] = useState<object | null>(
    null
  );

  const handleOpenDialog = (title: string, jsonString: string) => {
    setJsonDialogTitle(title);
    try {
      setJsonDialogContent(JSON.parse(jsonString));
    } catch {
      setJsonDialogContent({ error: "Invalid JSON" });
    }
    setJsonDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setJsonDialogOpen(false);
    setJsonDialogContent(null);
  };

  const fetchData = async () => {
    setLoading(true);

    try {
      const operation = filters.operationType.toUpperCase();
      const allowedOps = ["INSERT", "UPDATE"] as const;
      const isValidOp = allowedOps.includes(operation as any);

      const response = await audit({
        tableName: filters.tableName || undefined,
        operationType: isValidOp
          ? (operation as "INSERT" | "UPDATE")
          : undefined,
        accountId: filters.accountId ? Number.parseInt(filters.accountId) : undefined,
        userId: filters.userId ? Number.parseInt(filters.userId) : undefined,
        userName: filters.userName || undefined,
        startDate: filters.startDate ? new Date(filters.startDate) : undefined,
        endDate: filters.endDate ? new Date(filters.endDate) : undefined,
      });

      setData(
        response.audit.map((item: any) => ({
          recordId: item.recordId,
          createdBy: item.createdBy,
          createdOn: item.createdOn,
          schemaName: item.schemaName,
          tableName: item.tableName,
          operationType: item.operationType,
          originalValues: JSON.stringify(item.originalValues),
          newValues: JSON.stringify(item.newValues),
        }))
      );
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleApplyFilters = () => {
    setPage(0);
    fetchData();
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleOperationTypeChange = (event: SelectChangeEvent<string>) => {
    setFilters((prev) => ({ ...prev, operationType: event.target.value }));
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) return <Typography>A carregar...</Typography>;

  const visibleRows = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          {t("admin.audit.title")}
        </Typography>

        {/* Filtros */}
        <Grid2 container spacing={3}>
          <Grid2 size={4}>
            <TextField
              label={t("admin.audit.filters.account-id")}
              name="accountId"
              value={filters.accountId}
              onChange={handleFilterChange}
              fullWidth
            />
          </Grid2>
          <Grid2 size={4}>
            <TextField
              label={t("admin.audit.filters.user-id")}
              name="userId"
              value={filters.userId}
              onChange={handleFilterChange}
              fullWidth
            />
          </Grid2>
          <Grid2 size={4}>
            <TextField
              label={t("admin.audit.filters.user-name")}
              name="userName"
              value={filters.userName}
              onChange={handleFilterChange}
              fullWidth
            />
          </Grid2>

          <Grid2 size={6}>
            <TextField
              label={t("admin.audit.filters.table-name")}
              name="tableName"
              value={filters.tableName}
              onChange={handleFilterChange}
              fullWidth
            />
          </Grid2>
          <Grid2 size={6}>
            <FormControl fullWidth>
              <InputLabel>{t("admin.audit.filters.operation-type")}</InputLabel>
              <Select
                name="operationType"
                value={filters.operationType}
                onChange={handleOperationTypeChange}
                label={t("admin.audit.filters.operation-type")}
              >
                <MenuItem value="INSERT">INSERT</MenuItem>
                <MenuItem value="UPDATE">UPDATE</MenuItem>
              </Select>
            </FormControl>
          </Grid2>

          <Grid2 size={6}>
            <DatePicker
              label={t("admin.audit.filters.start-date")}
              value={filters.startDate ? moment(filters.startDate) : null}
              onChange={(newValue) =>
                setFilters((prev) => ({
                  ...prev,
                  startDate: newValue?.format("YYYY-MM-DD") || "",
                }))
              }
              format="DD/MM/YYYY"
              views={["day", "month", "year"]}
              slotProps={{
                textField: {
                  variant: "outlined",
                  fullWidth: true,
                },
              }}
            />
          </Grid2>

          <Grid2 size={6}>
            <DatePicker
              label={t("admin.audit.filters.end-date")}
              value={filters.endDate ? moment(filters.endDate) : null}
              onChange={(newValue) =>
                setFilters((prev) => ({
                  ...prev,
                  endDate: newValue?.format("YYYY-MM-DD") || "",
                }))
              }
              format="DD/MM/YYYY"
              views={["day", "month", "year"]}
              slotProps={{
                textField: {
                  variant: "outlined",
                  fullWidth: true,
                },
              }}
            />
          </Grid2>

          <Grid2>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApplyFilters}
            >
              {t("admin.audit.filters.apply-filters")}
            </Button>
          </Grid2>
        </Grid2>

        {/* Tabela */}
        <TableContainer>
          <Table sx={{ tableLayout: "auto", width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '5%' }}>
                  <strong>{t("admin.audit.table.record-id")}</strong>
                </TableCell>
                <TableCell sx={{ width: '5%' }}>
                  <strong>{t("admin.audit.table.created-by")}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t("admin.audit.table.created-on")}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t("admin.audit.table.schema-name")}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t("admin.audit.table.table-name")}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t("admin.audit.table.operation-type")}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t("admin.audit.table.original-values")}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t("admin.audit.table.new-values")}</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.recordId}</TableCell>
                  <TableCell>{item.createdBy}</TableCell>
                  <TableCell>
                    {new Date(item.createdOn).toLocaleDateString("pt-PT")}
                  </TableCell>
                  <TableCell>{item.schemaName}</TableCell>
                  <TableCell>{item.tableName}</TableCell>
                  <TableCell>{item.operationType}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="text"
                      color="primary"
                      onClick={() =>
                        handleOpenDialog(
                          t("admin.audit.table.original-values"),
                          item.originalValues
                        )
                      }
                    >
                      Ver detalhes
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="text"
                      color="primary"
                      onClick={() =>
                        handleOpenDialog(
                          t("admin.audit.table.new-values"),
                          item.newValues
                        )
                      }
                    >
                      Ver detalhes
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={8} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Paper sx={{ mt: 2, p: 1 }}>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={data.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Paper>

      {/* Dialog para JSON */}
      <Dialog open={jsonDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{jsonDialogTitle}</DialogTitle>
        <DialogContent dividers>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {JSON.stringify(jsonDialogContent, null, 2)}
          </pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="contained">Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditTable;
